/* Built In Imports */
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

/* External Imports */
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Text,
} from '@chakra-ui/react';
import { render } from 'datocms-structured-text-to-html-string';
import { useMediaQuery } from 'react-responsive';

/* Internal Imports */
/* Components */
import StructuredTextParser from '@msr/Utility/StructuredTextParser';
import { Downloadform } from '@msr/components/UI/Home/Host-guide/Download-form';
import { mixPanel } from '@utils/Mixpanel';

const commonButtonStyles = {
  className: 'orange-button',
  display: 'flex',
  fontWeight: '500',
  fontFamily: 'FedraSansStd-medium',
  fontSize: '16px',
  bgColor: 'yellow.100',
  minH: '50px',
  maxW: '244px',
  textAlign: 'center',
  px: '56px',
  py: '16px',
  borderRadius: '5px',
  color: 'black',
  _hover: { bg: 'fulvous' },
};

export const HostWatchPartyBanner = ({ sectionContent, pageData }) => {
  const router = useRouter();
  const Mixpanel = mixPanel(router.asPath);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const bannerData =
    sectionContent.find(item => item._modelApiKey === 'top_banner_event') ||
    sectionContent[0];

  const { title, description, button, mobileImage, desktopImage } = bannerData;

  const downloadData = pageData?.body?.[0]?.value?.[4]?.sectionContent?.find(
    item => item._modelApiKey === 'json_table'
  );

  // Function to check if hash is #host-guide-popup
  const checkHash = () => {
    if (isMobile && window.location.hash === '#host-guide-popup') {
      setIsDrawerOpen(true);
    } else {
      setIsDrawerOpen(false);
    }
  };

  useEffect(() => {
    if (!isMobile && window.location.hash === '#host-guide-popup') {
      history.replaceState(null, null, ' '); // Remove hash if not mobile
    }
    checkHash(); // Initial check

    window.addEventListener('hashchange', checkHash); // Listen for hash changes
    return () => {
      window.removeEventListener('hashchange', checkHash); // Cleanup
    };
  }, [isMobile]);

  const handleClose = () => {
    if (window.location.hash === '#host-guide-popup') {
      history.replaceState(null, null, ' '); // Clear hash without reloading
    }
    setIsDrawerOpen(false);
  };

  const handleHostGuide = () => {
    if (isMobile) {
      window.location.hash = 'host-guide-popup'; // Set hash in URL
    } else {
      const targetElement = document.getElementById(
        'download-host-guide-section'
      );
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
    Mixpanel.track('HWP_Download_hostguide');
  };

  const BannerContent = (
    <Box
      position="relative"
      maxW="659px"
      mx="auto"
      textAlign="center"
      justifyItems="center"
      id="msr-host-watch-party-banner"
    >
      <Text
        fontFamily="FedraSansStd-A-medium"
        fontWeight={500}
        fontSize={{ base: '28px', md: '48px' }}
        lineHeight={{ base: '33.6px', md: '57.6px' }}
        pb={{ base: '10px', md: '12px' }}
        maxW={{ base: '323px', md: '100%' }}
        justifySelf="center"
        margin="0 auto"
        textAlign="center"
      >
        {title}
      </Text>
      <Box
        maxW={{ base: '248px', sm: '410px' }}
        pb={{ base: '27px', md: '25px' }}
        margin="0 auto"
        textAlign="center"
      >
        <StructuredTextParser
          className="msr-banner-description"
          fontFamily="FedraSansStd-book"
          fontWeight={isMobile ? 400 : 350}
          fontSize="16px"
          lineHeight="24px"
          str={render(description)}
          textAlign="center"
          justifySelf="center"
          margin="0 auto"
        />
      </Box>
      {button?.[0]?.buttonText && (
        <Button
          onClick={handleHostGuide}
          {...commonButtonStyles}
          justifySelf="center"
          margin="0 auto"
          textAlign="center"
          height={'56px'}
        >
          {button[0].buttonText}
        </Button>
      )}
      <style jsx>
        {`
          .msr-banner-description p {
            color: #fff;
          }
        `}
      </style>
    </Box>
  );

  return (
    <>
      <Box
        w="100%"
        bgImage={{ base: mobileImage?.url, md: desktopImage?.url }}
        bgSize="cover"
        bgPos="center"
        bgRepeat="no-repeat"
        textAlign="center"
        height={{ base: '405px', md: 'auto' }}
        position="relative"
        pt="124px"
        pb="154px"
        color="white"
      >
        {!isMobile && (
          <Box position="absolute" inset="0" bg="black" opacity={0.7} />
        )}
        {!isMobile && BannerContent}
      </Box>
      {isMobile && (
        <>
          <Box bg="black" color="white" px="18px" pt="56px" pb="78px">
            {BannerContent}
          </Box>
          <Box>
            <Drawer
              placement="bottom"
              onClose={handleClose}
              isOpen={isDrawerOpen}
              closeOnOverlayClick={false}
            >
              <DrawerOverlay />
              <DrawerContent bgColor="#03062C" color="#fff">
                <DrawerCloseButton
                  border="1px solid #fff"
                  borderRadius="100%"
                  size="sm"
                  mt="10px"
                  onClick={handleClose}
                />
                <DrawerBody
                  pb="27px"
                  pt={{ base: '35px' }}
                  display={'flex'}
                  justifyContent={'center'}
                  alignContent={'center'}
                  width={'100%'}
                >
                  <Box width={'100%'} maxW={{ sm: '75%' }}>
                    <Downloadform
                      downloadData={downloadData}
                      isMobile={isMobile}
                      handleClose={handleClose}
                    />
                  </Box>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </Box>
        </>
      )}
    </>
  );
};
